import React, { useEffect } from 'react';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { useTranslation } from 'react-i18next';
import { ConfigType, ConfigurationElement, ConfProp } from '../../types/rest/configurationsType';
import BzmIcon from '../../components/icons/BzmIcon';
import i18n from '../../i18n';
import { copyToClipboard } from '../../components/buttons/CopyToClipboardButton';
import BzFormControlWithHook from '../../components/forms/BzFormControlWithHook';
import { CallbackFn, useFormInput, ValidatorType } from '../../hooks/useFormInput';
import { ColSizes } from './ConfigurationPropertyList';
import _ from 'lodash';
import DropdownButton from '../../components/ui/dropdown/DropdownButton';
import {
  BlazeDropdownItem,
  buildDropdownButtonToggle,
} from '../../components/ui/dropdown/DropdownButton/DropdownButton';
import ConfigurableAsset from './ConfigurableAsset/ConfigurableAsset';

interface ConfigurationPropertyProps {
  id: string;
  onChange: (confProp: ConfProp) => void;
  onDelete: () => void;
  configurationProperty: ConfProp;
  onAdd: (type: ConfigType) => void;
  showAddIcon: boolean;
  showDeleteIcon: boolean;
  colSizes: ColSizes;
  currentConfigurationKeys: string[];
  isReadOnly?: boolean;
  inInitialState: boolean;
}

const ConfigurationProperty = (props: ConfigurationPropertyProps) => {
  const { t } = useTranslation('configurations');
  const [fieldValue, setFieldValue] = React.useState(props.configurationProperty.configuration.value || undefined);
  useEffect(() => {
    if (props.configurationProperty.configuration.type === 'string') {
      setFieldValue(props.configurationProperty.configuration.value || '');
    }
  }, [props.configurationProperty.configuration.value]);

  const handleChangeValue = (e: any) => {
    const target = e.target as HTMLInputElement;
    const update = _.cloneDeep(props.configurationProperty);
    update.configuration.value = target.value;
    props.onChange(update);
    setFieldValue(target.value);
  };

  const handleChangeConfigurableAsset = (config: ConfigurationElement) => {
    props.onChange({ ...props.configurationProperty, configuration: config });
  };

  const handleChangeKey = (keyValue: any, isValid: boolean) => {
    const update = { ...props.configurationProperty, key: keyValue, isValid: isValid };
    props.onChange(update);
  };

  const { colSizes, isReadOnly } = props;

  useEffect(() => {
    fieldInput.resetValue(props.configurationProperty.key);
  }, [props.currentConfigurationKeys]);

  const fieldInput = useFormInput(
    _.get(props.configurationProperty, 'configuration.value'),
    (fn: CallbackFn) => {
      handleChangeKey(fn.value, fn.isValid);
    },
    {
      type: ValidatorType.REQUIRED_CONDITIONAL,
      value: props.configurationProperty.isDirty,
      invalidMessage: i18n.t('configurations:configurationsTable.validations.propertyRequired'),
    },
    {
      type: ValidatorType.INVALID_CHARACTERS,
      invalidMessage: i18n.t('configurations:configurationsTable.validations.propertyIllegalChars'),
    },
    {
      type: ValidatorType.MAX_LENGTH,
      value: 250,
      invalidMessage: i18n.t('configurations:configurationsTable.validations.propertyMaximumLength'),
    },
    {
      type: ValidatorType.VALUE_FOUND_ON_ARRAY,
      value: props.currentConfigurationKeys,
      invalidMessage: i18n.t('configurations:configurationsTable.validations.propertyAlreadyExists'),
    },
  );

  const createTemplateString = (prefix: string, value: string) => {
    return `\${${prefix}.${value}}`;
  };

  const copyIcon = (
    <BzmIcon
      title={t('properties.copyProperty')}
      onClick={() => {
        copyToClipboard(
          createTemplateString('config', props.configurationProperty.key),
          t('properties.copyDataSuccessMsg'),
        );
      }}
      className="icon-xs text-primary "
      variant={'icon-bzm-clipboard'}
    />
  );

  const dropdownItems: BlazeDropdownItem[] = [
    {
      content: 'Default',
      isHeading: false,
      fnCallback: () => props.onAdd('string'),
    },
    {
      content: 'Certificate',
      isHeading: false,
      fnCallback: () => props.onAdd('asset'),
    },
    {
      content: 'Messaging params',
      isHeading: false,
      fnCallback: () => props.onAdd('messaging'),
    },
  ];

  return (
    <Row className={'mb-4'}>
      <Col md={colSizes.md[0]} lg={colSizes.lg[0]}>
        <BzFormControlWithHook
          className={'propertyName-' + props.id}
          useFormInputHook={fieldInput}
          placeholder={t('properties.namePlaceholder')}
          required={true}
          value={props.configurationProperty.key}
          disabled={false}
          autoComplete={'false'}
          icon={copyIcon}
        />
      </Col>
      <Col md={colSizes.md[1]} lg={colSizes.lg[1]}>
        {/* String type (default) */}
        {props.configurationProperty.configuration.type === 'string' && (
          <Form.Control
            disabled={isReadOnly}
            name={'propValue'}
            as="input"
            className={props.configurationProperty.key === 'MSG_PASSWORD' ? 'passwordInput' : ''}
            type={'text'}
            autoComplete={'off'}
            onChange={handleChangeValue}
            placeholder={
              props.configurationProperty.key === 'MSG_PASSWORD'
                ? t('properties.passwordPlaceholder')
                : t('properties.valuePlaceholder')
            }
            value={fieldValue}
          />
        )}

        {/* Asset type (cert / jks) */}
        {props.configurationProperty.configuration.type === 'asset' && (
          <ConfigurableAsset
            configElement={props.configurationProperty.configuration}
            onChange={handleChangeConfigurableAsset}
          />
        )}
      </Col>
      <Col md={colSizes.md[2]} lg={colSizes.lg[2]}>
        {props.showDeleteIcon && (
          <Button
            variant="danger"
            id={'qa-btn-delete-prop-' + props.id}
            onClick={props.onDelete}
            className="btn-circular"
            title={t('properties.delete')}
          >
            <BzmIcon variant={'icon-bzm-delete'} />
          </Button>
        )}
        {props.showAddIcon && (
          <DropdownButton
            inline={true}
            dropdownItems={dropdownItems}
            dropDownPlacement={'LEFT'}
            customToggle={buildDropdownButtonToggle({
              title: 'Add New Property',
              toggleComponent: (
                <Button variant="outline-primary" id={'qa-btn-add-new-prop-' + props.id} className="btn-circular ml-4">
                  <BzmIcon variant={'icon-bzm-add'} />
                </Button>
              ),
            })}
            dropdownHeader={<span className={'font-size-sm'}>Configuration Property Types</span>}
            toggleTooltip={'Add New Property'}
          />
        )}
      </Col>
    </Row>
  );
};

export default ConfigurationProperty;
